import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPendingProcurementLines, createProcurementBatch } from '../../api/procurement';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import type { PendingProcurementGroup } from '../../types/api';

function money(value: number) {
  return `£${value.toFixed(2)}`;
}

// The review step Perry asked for: "create batch" used to commit straight
// into history with no chance to see what was actually being ordered.
// This shows the itemised breakdown first - only on explicit confirm does
// it call createProcurementBatch (which also marks the lines as ordered).
export function CreateBatchModal({
  group,
  open,
  onClose,
}: {
  group: PendingProcurementGroup | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const detailQuery = useQuery({
    queryKey: ['procurement-pending-lines', group?.customerOrganisationId, group?.siteId],
    queryFn: () => getPendingProcurementLines(group!.customerOrganisationId, group!.siteId),
    enabled: open && !!group,
  });

  const mutation = useMutation({
    mutationFn: () =>
      createProcurementBatch({ customerOrganisationId: group!.customerOrganisationId, siteId: group!.siteId }),
    onSuccess: (batch) => {
      queryClient.invalidateQueries({ queryKey: ['procurement-pending'] });
      queryClient.invalidateQueries({ queryKey: ['procurement-batches'] });
      queryClient.invalidateQueries({ queryKey: ['deliverable'] });
      showToast(`Batch created - ${batch.orderLines?.length ?? 0} line(s) marked ordered.`);
      onClose();
    },
    onError: () => {
      // surfaced inline below via mutation.error
    },
  });

  if (!group) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Review batch - ${group.customerName} / ${group.siteName}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={!detailQuery.data || detailQuery.data.lines.length === 0}
          >
            Confirm &amp; mark as ordered
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mutation.isError && <Alert tone="error">{apiErrorMessage(mutation.error, 'Could not create the batch.')}</Alert>}
        <p className="text-sm text-slate-500">
          This is everything currently approved and unbatched for this customer/site. Confirming marks it as ordered
          with the supplier and moves it into batch history - it can't be undone from here.
        </p>

        {detailQuery.isLoading ? (
          <div className="py-10 text-center">
            <Spinner />
          </div>
        ) : !detailQuery.data || detailQuery.data.lines.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            Nothing left to review - it may have just been batched elsewhere.
          </p>
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Item</Th>
                <Th>Qty</Th>
                <Th>Unit price</Th>
                <Th>Line total</Th>
                <Th>Order</Th>
                <Th>Requested by</Th>
              </tr>
            </Thead>
            <tbody>
              {detailQuery.data.lines.map((line) => (
                <Tr key={line.orderLineId}>
                  <Td className="font-medium text-slate-900">{line.itemName}</Td>
                  <Td>{line.quantity}</Td>
                  <Td>{money(line.unitPrice)}</Td>
                  <Td>{money(line.lineTotal)}</Td>
                  <Td>
                    <div className="flex flex-col">
                      <span>{line.orderReference}</span>
                      {line.lineReference && <span className="text-xs text-slate-400">{line.lineReference}</span>}
                    </div>
                  </Td>
                  <Td>{line.requestedBy ?? '-'}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}

        {detailQuery.data && detailQuery.data.lines.length > 0 && (
          <div className="flex justify-end border-t border-slate-100 pt-3 text-sm font-semibold text-slate-900">
            Total: {money(detailQuery.data.totalValue)}
          </div>
        )}
      </div>
    </Modal>
  );
}
